/**
 * @fileoverview Global saving-state indicator for the navbar.
 *
 * Reads the module-level `pendingSavesState` via `useSnapshot` and renders a
 * state derived from the store: save-failed (`failedSaveIds` non-empty) or
 * saving (`pendingIds` non-empty) while active, and "Saved HH:MM:SS" transiently
 * for `SAVED_VISIBLE_MS` after `lastSavedAt` advances (a clean drain or an
 * explicit ⌘S flush) — then it disappears. With no recent save activity it
 * renders nothing, so the navbar isn't permanently occupied.
 * "Save failed" stays until the failure is retried or the field unmounts; the
 * failure *message* is shown as a toast by `MessageProvider`.
 *
 * With `compact` (the navbar below `sm`) each state renders its icon only,
 * keeping its `aria-label`, and the text moves into a Tooltip, so the indicator
 * takes no fixed width and leaves the navbar search its floor.
 *
 * This is the only navbar subscriber to the store, so save activity re-renders
 * just this component and never the surrounding chrome — the point of replacing
 * per-field saving state.
 */

import { FC, CSSProperties, useEffect, useState } from 'react';
import { Typography, Tooltip, theme } from 'antd';
import { LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { pendingSavesState } from '../../store/pendingSavesStore';

const { Text } = Typography;

const SAVED_VISIBLE_MS = 5000;

const contentStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 5,
  minWidth: 80,
  whiteSpace: 'nowrap',
};

const compactStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
};

type SavingIndicatorProps = {
  compact?: boolean;
};

const SavingIndicator: FC<SavingIndicatorProps> = ({ compact = false }) => {
  const { token } = theme.useToken();
  const snap = useSnapshot(pendingSavesState);
  const saving = snap.pendingIds.size > 0;
  const failed = snap.failedSaveIds.size > 0;
  const lastSavedAt = snap.lastSavedAt;

  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (lastSavedAt == null) return;
    setShowSaved(true);
    const timeout = setTimeout(() => setShowSaved(false), SAVED_VISIBLE_MS);
    return () => clearTimeout(timeout);
  }, [lastSavedAt]);

  if (failed && compact) {
    return (
      <Tooltip title="Some changes couldn't be saved">
        <Text type="danger" style={compactStyle}>
          <ExclamationCircleOutlined aria-label="Save failed" />
        </Text>
      </Tooltip>
    );
  }

  if (failed) {
    return (
      <Tooltip title="Some changes couldn't be saved">
        <Text type="danger" style={contentStyle}>
          <ExclamationCircleOutlined aria-label="Save failed" />
          Save failed
        </Text>
      </Tooltip>
    );
  }

  if (saving && compact) {
    return (
      <Tooltip title="Saving…">
        <Text type="secondary" style={compactStyle}>
          <LoadingOutlined aria-label="Saving" />
        </Text>
      </Tooltip>
    );
  }

  if (saving) {
    return (
      <Text type="secondary" style={contentStyle}>
        <LoadingOutlined aria-label="Saving" />
        Saving…
      </Text>
    );
  }

  if (showSaved && lastSavedAt != null && compact) {
    return (
      <Tooltip title={`Saved ${new Date(lastSavedAt).toLocaleTimeString()}`}>
        <Text type="secondary" style={compactStyle}>
          <CheckCircleOutlined aria-label="Saved" style={{ color: token.colorSuccess }} />
        </Text>
      </Tooltip>
    );
  }

  if (showSaved && lastSavedAt != null) {
    return (
      <Text type="secondary" style={contentStyle}>
        <CheckCircleOutlined aria-label="Saved" style={{ color: token.colorSuccess }} />
        {`Saved ${new Date(lastSavedAt).toLocaleTimeString()}`}
      </Text>
    );
  }

  return null;
};

export default SavingIndicator;
